/**
 * PORT (touch screens with the wide view): the battle's ultra scanner (menu_scaner: the enemy teams' weapons, the enemy
 * worms' skills and perks) for fingers, laid out by the wide screen (ui/wide-screen.ts, ScreenDress.custom).
 *
 * The original's scanner is a 511x511 panel in its 807x600 window: a header ("Показать :", the tabs "Команды" and
 * "Черви"), a list of rows 96 px tall 120 px apart (a team: its name and weapons; a worm: its team, name, attack,
 * protection, health and perks), scrolled by a slider of 16x30 px, text of 12 px. On a phone in landscape (0.53-0.69
 * CSS px per px) that is 6-8 CSS px text and 20-23 CSS px tabs in the middle of a much wider screen, the battle's HUD
 * showing around it. With the touch controls and the wide view (a phone, a tablet):
 *  - the panel's frame spans the shown safe area (its corners and borders as drawn, its flat inside stretched: pieces
 *    of its art; the plant at its upper right, the sticks at its lower left), the HUD hidden under it (wide-screen.ts
 *    `under`);
 *  - the header and the rows are scaled until their text measures TEXT_CSS CSS px (MIN_TEXT_CSS where that shows more
 *    rows at once), never smaller than the original draws them; the tabs take taps on HIT_CSS CSS px;
 *  - the header lies above the list, or beside it (the tabs one under the other) where that shows more rows at once (a
 *    20:9 phone: three worms), whichever tab is shown;
 *  - the rows lie in as many columns as fit left of the touch controls' close button and the plant, as many rows as the
 *    room holds; a longer list scrolls by a finger dragged over it, a bar beside it showing where it is (the original's
 *    slider is hidden);
 *  - the touch controls' menu info (an online battle's clock) gets a place of its own (FWP.menu_info_slot): right of
 *    the tabs, or under them;
 *  - the scanner's close arrow lies under the touch controls' close button (ui/battle-sheet.ts placeCloseUnder).
 * Drawing, placement and scrolling only: the rows, the tabs and their clicks are the game's. The rows of the teams,
 * which the game adds to the panel itself, go into the list's container to be clipped and scrolled with it.
 */
import { MouseEvent, Point, Sprite } from '@flash';
import { type Orig, type Piece, type ScreenContext, type ScreenLayout, closeRect, pieceOf, placeCloseUnder, remember, restore, touchSheetOn } from './battle-sheet';

/** A rectangle in the scanner's (the stage's) coordinates. */
interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The text size aimed at (CSS px), the least it may take for more rows at once, and the original's (px). */
export const TEXT_CSS = 13;
export const MIN_TEXT_CSS = 12;
const TEXT_PX = 12;
/** The touch target of the tabs (CSS px). */
export const HIT_CSS = 48;

/** The scanner's parts (character ids). */
const FRAME_ID = 577;
const PLANT_ID = 65;
const STICKS_ID = 67;
const TRACK_ID = 578;
const CLIP_ID = 579;
const LINE_ID = 580;
const TEAMS_LABEL_ID = 581;
const WORMS_LABEL_ID = 582;
const SHOW_LABEL_ID = 583;
/** A team's row, which the game adds to the panel itself (clip_scaner_team). */
const TEAM_ROW_ID = 1494;
/** The frame's corners kept whole (its pale rim and the green line inside it). */
const EDGE = 16;
/** The frame's border and the room inside it (px at scale 1 / CSS px). */
const BORDER = 12;
const PAD_CSS = 8;
/** The rows' gaps (px at scale 1; the original's rows are 24 px apart). */
const GAP_X = 24;
const GAP_Y = 16;
/** The header's parts as the original draws them (px): its labels' fields (117 x 20), the tabs' height. */
const LABEL_W = 117;
const LABEL_H = 20;
const TAB_H = 34;
/** The scroll bar's width and its gap to the rows (CSS px). */
const BAR_CSS = 6;
const BAR_GAP_CSS = 8;

type NinePart = 'c' | 't' | 'b' | 'l' | 'r' | 'tl' | 'tr' | 'bl' | 'br';

interface ScanState {
  on: boolean;
  orig: Map<any, Orig>;
  /** The frame's art at scale 1 (the scanner's coordinates) and its pieces. */
  frame: any;
  fb: Area | null;
  pieces: Record<NinePart, Piece | null> | null;
  /** The header's parts and their box at scale 1. */
  header: { label: any; tabs: Array<{ btn: any; text: any }>; line: any; box: Area } | null;
  /** The tabs' own hit states (restored when the layout is left). */
  hits: Map<any, any>;
  /** The list's viewport, its content's height, the scroll, the rows' pitch and scale. */
  view: Area | null;
  contentH: number;
  scroll: number;
  k: number;
  /** A finger dragging the list: where it went down and the scroll then. */
  drag: { y: number; scroll: number } | null;
  thumb: any;
  /** The place kept for the touch controls' menu info (null: none). */
  info: Area | null;
  /** What the layout was made for: the screen's shape, the close button, the rows. */
  key: string;
}

function stateOf(root: any): ScanState {
  if (!root.$touchScan)
    root.$touchScan = { on: false, orig: new Map(), frame: null, fb: null, pieces: null, header: null, hits: new Map(), view: null, contentH: 0, scroll: 0, k: 1, drag: null, thumb: null, info: null, key: '' } as ScanState;
  return root.$touchScan as ScanState;
}

function childById(root: any, id: number): any {
  for (let i = 0; i < root.numChildren; i++) if (root.getChildAt(i).$charId === id) return root.getChildAt(i);
  return null;
}

/** The place kept for the touch controls' menu info while the scanner `root` has its touch layout (null: none). */
export function touchScannerInfo(root: any): Area | null {
  const st = root && (root.$touchScan as ScanState | undefined);
  return st && st.on ? st.info : null;
}

/**
 * The list's rows now, top to bottom: the worms' (in scaner_container) or the teams' (the game adds them to the panel:
 * moved into the container). A render of the scanner again adds its rows again at the same places: of those the last.
 */
function rowsOf(root: any): any[] {
  const cont = root.scaner_container;
  if (!cont) return [];
  for (let i = root.numChildren - 1; i >= 0; i--) {
    const c = root.getChildAt(i);
    if (c.$charId !== TEAM_ROW_ID) continue;
    // (the game's place: its row's y; in the container at the same scale)
    c.$scanY = c.y;
    cont.addChildAt(c, 0);
  }
  const byPlace = new Map<number, any>();
  for (let i = 0; i < cont.numChildren; i++) {
    const c = cont.getChildAt(i);
    if (c.$scanY === undefined) c.$scanY = c.y;
    const was = byPlace.get(c.$scanY);
    if (was) was.visible = false;
    byPlace.set(c.$scanY, c);
  }
  return [...byPlace.entries()].sort((a, b) => a[0] - b[0]).map((e) => e[1]);
}

/** Moves `o` (at its scale now) so that its art's top left corner lies at (x, y). */
function putAt(o: any, root: any, x: number, y: number): void {
  const b = o.getBounds(root);
  o.x += x - b.x;
  o.y += y - b.y;
}

/** A tab's hit area of at least `w` x `h` px (the scanner's coordinates) round its art, or its own again (null). */
function setTabHit(st: ScanState, btn: any, root: any, size: { w: number; h: number } | null): void {
  if (!st.hits.has(btn)) st.hits.set(btn, btn.hitTestState);
  btn.hitTestState = st.hits.get(btn);
  if (!size) {
    st.hits.delete(btn);
    return;
  }
  // (the tab shown as text measured as a button too)
  const vis = btn.visible;
  btn.visible = true;
  const b = btn.getBounds(root);
  btn.visible = vis;
  const w = Math.max(b.width, size.w);
  const h = Math.max(b.height, size.h);
  const p0 = btn.globalToLocal(root.localToGlobal(new Point(b.x + b.width / 2 - w / 2, b.y + b.height / 2 - h / 2)));
  const p1 = btn.globalToLocal(root.localToGlobal(new Point(b.x + b.width / 2 + w / 2, b.y + b.height / 2 + h / 2)));
  const hit = new Sprite();
  hit.graphics.beginFill(0, 0);
  hit.graphics.drawRect(Math.min(p0.x, p1.x), Math.min(p0.y, p1.y), Math.abs(p1.x - p0.x), Math.abs(p1.y - p0.y));
  hit.graphics.endFill();
  btn.hitTestState = hit;
}

/** The scanner's parts found once (the frame, the header): false when it is not the scanner as the game draws it. */
function prepare(st: ScanState, root: any): boolean {
  if (st.header && st.frame) return true;
  const frame = childById(root, FRAME_ID);
  const label = childById(root, SHOW_LABEL_ID);
  const tl = childById(root, TEAMS_LABEL_ID);
  const wl = childById(root, WORMS_LABEL_ID);
  const line = childById(root, LINE_ID);
  const teams = root.scaner_teams;
  const worms = root.scaner_worms;
  if (!frame || !label || !tl || !wl || !teams || !worms || !root.scaner_container) return false;
  st.frame = frame;
  const fb = frame.getBounds(root);
  st.fb = { x: fb.x, y: fb.y, width: fb.width, height: fb.height };
  const parts = [label, teams, worms, tl, wl, line].filter(Boolean);
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const o of parts) {
    // (a hidden tab measured as shown)
    const v = o.visible;
    o.visible = true;
    const b = o.getBounds(root);
    o.visible = v;
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.width);
    y1 = Math.max(y1, b.y + b.height);
  }
  st.header = { label, tabs: [{ btn: teams, text: tl }, { btn: worms, text: wl }], line, box: { x: x0, y: y0, width: x1 - x0, height: y1 - y0 } };
  const at = root.getChildIndex(frame) + 1;
  const P = {} as Record<NinePart, Piece | null>;
  let i = at;
  // (the stretched inside first, the borders and corners over its ends)
  for (const k of ['c', 't', 'b', 'l', 'r', 'tl', 'tr', 'bl', 'br'] as NinePart[]) {
    P[k] = pieceOf(frame, 'scan_' + k);
    if (P[k]) root.addChildAt(P[k]!.sprite, i++);
  }
  st.pieces = P;
  const thumb = new Sprite();
  thumb.name = '$wide_scan_thumb';
  thumb.mouseEnabled = false;
  thumb.visible = false;
  const track = childById(root, TRACK_ID);
  root.addChildAt(thumb, track ? root.getChildIndex(track) + 1 : i);
  st.thumb = thumb;
  // A finger dragging the list scrolls it (frame() follows it while it is down).
  root.addEventListener(MouseEvent.MOUSE_DOWN, () => {
    const v = st.view;
    if (!st.on || !v || st.contentH <= v.height) return;
    const x = root.mouseX;
    const y = root.mouseY;
    if (x < v.x || x > v.x + v.width || y < v.y || y > v.y + v.height) return;
    st.drag = { y, scroll: st.scroll };
    const stage = root.stage;
    if (!stage) return;
    const up = () => {
      st.drag = null;
      stage.removeEventListener(MouseEvent.MOUSE_UP, up);
    };
    stage.addEventListener(MouseEvent.MOUSE_UP, up);
  });
  return true;
}

/** The frame's nine pieces over `d` (null: hidden). */
function placeFrame(st: ScanState, d: Area | null): void {
  const P = st.pieces!;
  const f = st.fb!;
  const E = EDGE;
  const src = (x: number, y: number, w: number, h: number): Area => ({ x, y, width: w, height: h });
  if (!d) {
    for (const p of Object.values(P)) p?.place(f, null);
    return;
  }
  const sx = [f.x, f.x + E + 4, f.x + f.width - E];
  const sy = [f.y, f.y + E + 4, f.y + f.height - E];
  const sw = [E, 1, E];
  const dx = [d.x, d.x + E - 0.5, d.x + d.width - E];
  const dw = [E, d.width - 2 * E + 1, E];
  const dy = [d.y, d.y + E - 0.5, d.y + d.height - E];
  const dh = [E, d.height - 2 * E + 1, E];
  const names: NinePart[][] = [
    ['tl', 't', 'tr'],
    ['l', 'c', 'r'],
    ['bl', 'b', 'br'],
  ];
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) P[names[r][c]]?.place(src(sx[c], sy[r], sw[c], sw[r]), src(dx[c], dy[r], dw[c], dh[r]));
}

/** The scanner as the original draws it (the touch layout left). */
function leave(st: ScanState, root: any): void {
  if (!st.on) return;
  st.on = false;
  st.drag = null;
  st.view = null;
  st.info = null;
  st.key = '';
  restore(st.orig, root);
  placeFrame(st, null);
  if (st.frame) st.frame.visible = true;
  if (st.thumb) st.thumb.visible = false;
  const track = childById(root, TRACK_ID);
  if (track) track.visible = true;
  for (const id of [STICKS_ID, PLANT_ID]) {
    const o = childById(root, id);
    if (o) o.visible = true;
  }
  if (st.header?.line) st.header.line.visible = true;
  for (const t of st.header?.tabs ?? []) setTabHit(st, t.btn, root, null);
  // The rows where the game put them (the teams' back on the panel).
  const cont = root.scaner_container;
  for (let i = cont ? cont.numChildren - 1 : -1; i >= 0; i--) {
    const c = cont.getChildAt(i);
    if (c.$charId === TEAM_ROW_ID) {
      root.addChild(c);
      c.x = 181;
    } else c.x = 0;
    if (c.$scanY !== undefined) c.y = c.$scanY;
  }
}

/** The layout of the list for an arrangement of the header and a scale: how many rows it shows at once. */
interface Plan {
  side: boolean;
  k: number;
  cols: number;
  rows: number;
  /** The header's (or the column of tabs') room, the list's viewport (the rows' block within it). */
  head: Area;
  view: Area;
}

function place(st: ScanState, root: any, ctx: ScreenContext): void {
  const { stage, fwp } = ctx;
  const css = stage.cssScale > 0 ? stage.cssScale : 1;
  const u = 1 / css;
  // The frame over all the screen shows (nothing of the battle beside it), its content within the safe area.
  const V = (stage.visibleRect ?? stage.safeRect) as Area;
  const S = stage.safeRect as Area;
  const X0 = V.x;
  const X1 = V.x + V.width;
  const Y0 = V.y;
  const Y1 = V.y + V.height;
  st.frame.visible = false;
  placeFrame(st, { x: X0, y: Y0, width: X1 - X0, height: Y1 - Y0 });
  const pad = BORDER + PAD_CSS * u;
  const IX0 = Math.max(X0, S.x) + pad;
  const IY0 = Math.max(Y0, S.y) + pad;
  const IY1 = Math.min(Y1, S.y + S.height) - pad;
  // The plant at the frame's upper right, the sticks at its lower left, as far from its corners as in the original.
  const f = st.fb!;
  const plant = childById(root, PLANT_ID);
  let right = Math.min(X1, S.x + S.width) - pad;
  if (plant) {
    const o = st.orig.get(plant)!;
    plant.visible = true;
    plant.x = o.x + X1 - (f.x + f.width);
    plant.y = o.y + Y0 - f.y;
    // (it hung over the panel's right side: within the screen's edge, which the frame reaches)
    const pb = plant.getBounds(root);
    const over = pb.x + pb.width - (X1 - 2 * u);
    if (over > 0) plant.x -= over;
    right = Math.min(right, plant.getBounds(root).x - BAR_GAP_CSS * u);
  }
  const sticks = childById(root, STICKS_ID);
  if (sticks) {
    const o = st.orig.get(sticks)!;
    sticks.x = o.x + X0 - f.x;
    sticks.y = o.y + Y1 - (f.y + f.height);
  }
  // Left of the touch controls' close button (over the frame's right part).
  const cr = closeRect(fwp);
  if (cr && cr.x < right + BAR_GAP_CSS * u) right = Math.min(right, cr.x - BAR_GAP_CSS * u);
  // The rows' block ends before the scroll bar.
  const bar = (BAR_CSS + BAR_GAP_CSS) * u;
  const H = st.header!;
  const rows = rowsOf(root);
  let rw = 0;
  let rx = 0;
  for (const r of rows) {
    const b = r.getBounds(r);
    rw = Math.max(rw, b.x + b.width);
    rx = Math.min(rx, b.x);
  }
  // (a team's row is 410 px wide, a worm's 420: the same block for both, nothing moves when the tab changes)
  const RW = Math.max(420, rw - rx);
  const RH = 96;
  const k13 = Math.max(1, TEXT_CSS / (TEXT_PX * css));
  const k12 = Math.max(1, MIN_TEXT_CSS / (TEXT_PX * css));
  const plans: Plan[] = [];
  for (const side of [false, true]) {
    for (const k of k12 < k13 ? [k13, k12] : [k13]) {
      const hitH = Math.max(HIT_CSS * u, TAB_H * k);
      let head: Area;
      let lx0: number;
      let ly0: number;
      if (!side) {
        head = { x: IX0, y: IY0, width: H.box.width * k, height: hitH };
        lx0 = IX0;
        ly0 = IY0 + hitH + PAD_CSS * u;
      } else {
        // The label, then the tabs one under the other (their hit areas apart).
        const w = LABEL_W * k;
        head = { x: IX0, y: IY0, width: w, height: LABEL_H * k + PAD_CSS * u + 2 * hitH + PAD_CSS * u };
        lx0 = IX0 + w + 2 * PAD_CSS * u;
        ly0 = IY0;
      }
      const W = right - bar - lx0;
      const Hh = IY1 - ly0;
      if (RW * k > W || RH * k > Hh) continue;
      const cols = Math.max(1, Math.floor((W + GAP_X * k) / ((RW + GAP_X) * k)));
      const n = Math.max(1, Math.floor((Hh + GAP_Y * k) / ((RH + GAP_Y) * k)));
      plans.push({ side, k, cols, rows: n, head, view: { x: lx0, y: ly0, width: W, height: Hh } });
    }
  }
  // The most rows at once; of equals the larger text, the header above the list.
  plans.sort((a, b) => b.cols * b.rows - a.cols * a.rows || b.k - a.k || Number(a.side) - Number(b.side));
  let plan = plans[0];
  if (!plan) {
    // (a screen too small for a row at the original's size: the original's scale, the header above)
    const hitH = Math.max(HIT_CSS * u, TAB_H);
    plan = { side: false, k: 1, cols: 1, rows: 1, head: { x: IX0, y: IY0, width: H.box.width, height: hitH }, view: { x: IX0, y: IY0 + hitH + PAD_CSS * u, width: Math.max(1, right - bar - IX0), height: Math.max(1, IY1 - IY0 - hitH - PAD_CSS * u) } };
  }
  const k = plan.k;
  st.k = k;
  // The rows' block, and the header beside or above it, in the middle of the room left of the close button.
  const blockW = plan.cols * RW * k + (plan.cols - 1) * GAP_X * k;
  const fullW = (plan.side ? plan.view.x - IX0 : 0) + Math.max(blockW, plan.side ? 0 : plan.head.width);
  const shift = Math.max(0, (right - bar - IX0 - fullW) / 2);
  const bx0 = plan.view.x + shift;
  const hx0 = plan.head.x + shift;
  // The header.
  const place1 = (o: any, x: number, y: number) => {
    const w = st.orig.get(o)!;
    o.scaleX = w.sx * k;
    o.scaleY = w.sy * k;
    o.x = x + (w.x - H.box.x) * k;
    o.y = y + (w.y - H.box.y) * k;
  };
  const hy = plan.head.y + (plan.side ? 0 : (plan.head.height - H.box.height * k) / 2);
  if (!plan.side) {
    for (const o of [H.label, H.line, ...H.tabs.flatMap((t) => [t.btn, t.text])]) if (o) place1(o, hx0, hy);
    if (H.line) H.line.visible = true;
  } else {
    // The label at the top, each tab (its button, its text over it) under it, in the middle of the column.
    const lo = st.orig.get(H.label)!;
    H.label.scaleX = lo.sx * k;
    H.label.scaleY = lo.sy * k;
    putAt(H.label, root, hx0, hy);
    const hitH = Math.max(HIT_CSS * u, TAB_H * k);
    let ty = hy + LABEL_H * k + PAD_CSS * u;
    for (const t of H.tabs) {
      const bo = st.orig.get(t.btn)!;
      const to = st.orig.get(t.text)!;
      t.btn.scaleX = bo.sx * k;
      t.btn.scaleY = bo.sy * k;
      t.text.scaleX = to.sx * k;
      t.text.scaleY = to.sy * k;
      const vis = t.btn.visible;
      t.btn.visible = true;
      const b = t.btn.getBounds(root);
      t.btn.visible = vis;
      const dx = hx0 + (plan.head.width - b.width) / 2 - b.x;
      const dy = ty + (hitH - b.height) / 2 - b.y;
      t.btn.x += dx;
      t.btn.y += dy;
      // (the text where it is over its button in the original)
      t.text.x = t.btn.x + (to.x - bo.x) * k;
      t.text.y = t.btn.y + (to.y - bo.y) * k;
      ty += hitH + PAD_CSS * u;
    }
    // (the line under the tab shown as text belongs to the tabs side by side)
    if (H.line) H.line.visible = false;
  }
  for (const t of H.tabs) setTabHit(st, t.btn, root, { w: HIT_CSS * u, h: HIT_CSS * u });
  // The list: its rows in the grid, the container scaled; its clip over the viewport.
  const cont = root.scaner_container;
  cont.scaleX = cont.scaleY = k;
  rows.forEach((r, i) => {
    r.x = (i % plan.cols) * (RW + GAP_X) - rx;
    r.y = Math.floor(i / plan.cols) * (RH + GAP_Y);
  });
  const lines = Math.ceil(rows.length / plan.cols);
  st.contentH = lines > 0 ? (lines * (RH + GAP_Y) - GAP_Y) * k : 0;
  st.view = { x: bx0, y: plan.view.y, width: blockW, height: plan.view.height };
  // The sticks only where the rows never come (beside the tabs' column; not under the list of a small phone).
  if (sticks) {
    const b = sticks.getBounds(root);
    const v = st.view;
    sticks.visible = !(b.x < v.x + v.width && v.x < b.x + b.width && b.y < v.y + v.height && v.y < b.y + b.height);
  }
  const clip = childById(root, CLIP_ID);
  if (clip) {
    const o = st.orig.get(clip)!;
    clip.scaleX = o.sx;
    clip.scaleY = o.sy;
    // (its art in its own coordinates, scaled over the viewport, a little wider: the rows' art at their sides)
    const b = clip.getBounds(clip);
    const cx = st.view.x - 2 * u;
    const cw = st.view.width + 4 * u;
    clip.scaleX = cw / b.width;
    clip.scaleY = st.view.height / b.height;
    clip.x = cx - b.x * clip.scaleX;
    clip.y = st.view.y - b.y * clip.scaleY;
  }
  // The menu info's place: under the tabs in their column; right of the tabs side by side; else (a small phone) in
  // the upper right corner right of the list, the plant under it (hidden where it would reach the close button).
  st.info = null;
  if (plan.side) st.info = { x: hx0, y: plan.head.y + plan.head.height + PAD_CSS * u, width: plan.head.width, height: 26 * u };
  else {
    const x = hx0 + H.box.width * k + 2 * PAD_CSS * u;
    if (right - x >= 80 * u) st.info = { x, y: plan.head.y, width: right - x, height: plan.head.height };
    else {
      const x0 = st.view.x + st.view.width + (BAR_CSS + 2 * BAR_GAP_CSS) * u;
      const x1 = Math.min(X1, S.x + S.width) - BORDER - 4 * u;
      if (x1 - x0 >= 56 * u) st.info = { x: x0, y: IY0, width: x1 - x0, height: 36 * u };
      if (st.info && plant) {
        const pb = plant.getBounds(root);
        const down = st.info.y + st.info.height + 4 * u - pb.y;
        if (down > 0) plant.y += down;
        const b = plant.getBounds(root);
        plant.visible = !(cr && b.y + b.height > cr.y - 4 * u && b.x < cr.x + cr.width && cr.x < b.x + b.width);
      }
    }
  }
  scrollTo(st, root, st.scroll, u);
}

/** The list scrolled to `s` (clamped), the bar beside it showing where. */
function scrollTo(st: ScanState, root: any, s: number, u: number): void {
  const v = st.view;
  const cont = root.scaner_container;
  if (!v || !cont) return;
  const max = Math.max(0, st.contentH - v.height);
  st.scroll = Math.max(0, Math.min(max, s));
  cont.x = v.x;
  cont.y = v.y - st.scroll;
  const track = childById(root, TRACK_ID);
  const bx = v.x + v.width + BAR_GAP_CSS * u;
  const t = st.thumb;
  if (max <= 0) {
    if (track) track.visible = false;
    if (t) t.visible = false;
    return;
  }
  if (track) {
    const o = st.orig.get(track)!;
    track.visible = true;
    track.scaleX = o.sx;
    track.scaleY = o.sy;
    const b = track.getBounds(root);
    track.scaleY = (o.sy * v.height) / b.height;
    const c = track.getBounds(root);
    track.x += bx + (BAR_CSS * u - c.width) / 2 - c.x;
    track.y += v.y - c.y;
  }
  if (t) {
    const h = Math.max(32 * u, (v.height * v.height) / st.contentH);
    const y = v.y + ((v.height - h) * st.scroll) / max;
    t.visible = true;
    t.graphics.clear();
    t.graphics.beginFill(0x7ea84a, 1);
    t.graphics.drawRoundRect(bx, y, BAR_CSS * u, h, BAR_CSS * u, BAR_CSS * u);
    t.graphics.endFill();
  }
}

/** What the layout depends on: the screen's shape, the close button, the rows shown. */
function keyOf(root: any, ctx: ScreenContext): string {
  const st = ctx.stage;
  const r = st.safeRect;
  const cr = closeRect(ctx.fwp);
  const cont = root.scaner_container;
  let rows = 0;
  for (let i = 0; i < root.numChildren; i++) if (root.getChildAt(i).$charId === TEAM_ROW_ID) rows += 1000;
  if (cont) for (let i = 0; i < cont.numChildren; i++) if (cont.getChildAt(i).visible) rows++;
  return `${r.x.toFixed(1)},${r.y.toFixed(1)},${r.width.toFixed(1)},${r.height.toFixed(1)}|${st.cssScale}|${cr ? `${Math.round(cr.x)},${Math.round(cr.y)},${Math.round(cr.width)}` : ''}|${rows}`;
}

export const SCANNER: ScreenLayout = {
  layout(root, ctx) {
    const st = stateOf(root);
    const { stage, fwp } = ctx;
    const touch = !!fwp && typeof fwp.touch_zones === 'function';
    // Without the wide view (a tablet at ?wide=0): the original's scanner, its close arrow under the touch controls'.
    if (!touchSheetOn(stage, fwp) || !prepare(st, root)) {
      leave(st, root);
      placeCloseUnder(root.close_scaner, touch ? closeRect(fwp) : null, st.orig, root);
      st.key = '';
      return;
    }
    if (!st.on) {
      for (const id of [FRAME_ID, PLANT_ID, STICKS_ID, TRACK_ID, CLIP_ID]) remember(st.orig, childById(root, id), root);
      const H = st.header!;
      for (const o of [H.label, H.line, root.scaner_container, root.slider, ...H.tabs.flatMap((t) => [t.btn, t.text])]) remember(st.orig, o, root);
      st.on = true;
    }
    place(st, root, ctx);
    placeCloseUnder(root.close_scaner, closeRect(fwp), st.orig, root);
    // (after the teams' rows went into the list)
    st.key = keyOf(root, ctx);
  },
  frame(root, ctx) {
    const st = stateOf(root);
    if (!st.on) {
      // (the touch controls laid out after the stage's resize: the close arrow under their close button)
      const cr = closeRect(ctx.fwp);
      const ck = cr ? `${Math.round(cr.x)},${Math.round(cr.y)},${Math.round(cr.width)}` : '';
      if (ck !== (root.$scanCloseKey ?? '')) {
        root.$scanCloseKey = ck;
        SCANNER.layout(root, ctx);
      }
      return;
    }
    // The game hides or shows its slider as the list's length asks: the bar beside the list stands for it.
    if (root.slider && root.slider.visible) root.slider.visible = false;
    // New rows (the game renders the list after the scanner is on the stage), a new screen shape or close button.
    if (keyOf(root, ctx) !== st.key) {
      SCANNER.layout(root, ctx);
      return;
    }
    if (st.drag) {
      const css = ctx.stage.cssScale > 0 ? ctx.stage.cssScale : 1;
      const s = st.drag.scroll - (root.mouseY - st.drag.y);
      if (Math.abs(s - st.scroll) > 0.25) scrollTo(st, root, s, 1 / css);
    }
  },
};
